/**
 * JWT session management.
 * Access token: in-memory ONLY (never sessionStorage/localStorage) to limit XSS exposure.
 * Persistence across a hard reload comes from the httpOnly refresh cookie + silent refresh
 * (story #208), not from web storage.
 */
import {
  login as apiLogin,
  logout as apiLogout,
  refreshAccessToken,
  setAccessToken,
  registerUnauthorizedHandler,
} from "./api/building";

// Canonical role keys (ADR-012), matching building-domain's `RbacRole`
// (services/building/src/building_domain/domain/models.py).
export type RbacRole =
  "owner" | "operations" | "service" | "configuration" | "digital_team";

export interface Session {
  token: string;
  email: string;
  rbacRole: RbacRole;
}

let _session: Session | null = null;

// Clear the in-memory session on a 401 so the login-page guard doesn't bounce back to "/" on the
// same JS runtime. The api layer already dropped the in-memory access token in handleUnauthorized.
registerUnauthorizedHandler(() => {
  _session = null;
});

function _parseToken(token: string): Omit<Session, "token"> {
  const [, payload] = token.split(".");
  const decoded = JSON.parse(
    atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
  );
  return {
    email: decoded.email as string,
    rbacRole: decoded.rbac_role as RbacRole,
  };
}

function _isExpired(token: string): boolean {
  try {
    const [, payload] = token.split(".");
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    );
    const exp = decoded.exp as number | undefined;
    return exp !== undefined && Date.now() / 1000 >= exp;
  } catch {
    return true;
  }
}

// Seed both the in-memory session and the api layer's in-memory access token from a fresh JWT.
function _seed(token: string): Session {
  setAccessToken(token);
  _session = { token, ..._parseToken(token) };
  return _session;
}

export function getSession(): Session | null {
  if (!_session) return null;
  if (_isExpired(_session.token)) {
    _session = null;
    setAccessToken(null);
    return null;
  }
  return _session;
}

export async function login(email: string, password: string): Promise<Session> {
  const { access_token } = await apiLogin(email, password);
  const session = _seed(access_token);
  window.dispatchEvent(new Event("kairos:login"));
  return session;
}

// Silent refresh via the httpOnly refresh cookie — reseeds the in-memory session on success.
// Returns null (and clears the session) if the cookie is missing/expired/rotated.
export async function refresh(): Promise<Session | null> {
  const token = await refreshAccessToken();
  if (!token) {
    _session = null;
    return null;
  }
  try {
    return _seed(token);
  } catch {
    _session = null;
    setAccessToken(null);
    return null;
  }
}

export async function logout(): Promise<void> {
  await apiLogout();
  _session = null;
}

// Flat membership check against the operate-write capability set (ADR-012) — never a
// rank/`>=` comparison — mirrors building-domain's `_OPERATE_WRITE_ROLES`
// (adapters/http/deps.py). No role inherits another's writes.
const OPERATE_WRITE_ROLES: ReadonlySet<RbacRole> = new Set([
  "owner",
  "operations",
  "service",
  "configuration",
]);

export function isOperator(): boolean {
  const role = getSession()?.rbacRole;
  return role !== undefined && OPERATE_WRITE_ROLES.has(role);
}

// Configure-write set. Distinct from isOperator() — configuration-scoped actions
// (variant selection, KB behaviour activation, equipment dependencies) must gate on
// this, never on isOperator(). Phase-admin JWT is `configuration` (UI: Ylläpitäjä).
// Mirrors building-domain's `_CONFIGURE_WRITE_ROLES` (adapters/http/deps.py).
const CONFIGURE_WRITE_ROLES: ReadonlySet<RbacRole> = new Set(["configuration"]);

export function isConfigurator(): boolean {
  const role = getSession()?.rbacRole;
  return role !== undefined && CONFIGURE_WRITE_ROLES.has(role);
}
